import { formatMoney } from '@/utils/money-util'
import { match } from '@/utils/pattern-match-util'
import { timeago, dateFormatters } from '@/utils/date-util'
import {
  type ScopeChangePriceBreakDownItemForAdmin,
  type ScopeChangeRequest,
  type ScopeChangeRequestStatus,
} from './ScopeChangeRequest'

/**
 * The tone a request's status is shown in.
 */
export type ScopeChangeRequestStatusTone =
  'Accepted' | 'Rejected' | 'Pending' | 'Neutral'

/**
 * Formats the phase to a string
 * @param phase
 */
export function formatPhase(phase: ScopeChangeRequestStatus) {
  return match(phase, {
    Accepted: () => 'Accepted',
    Rejected: () => 'Rejected',
    ApplyFailed: () => 'Rejected',
    PendingFinalization: () => 'Finalizing',
    PendingAdminApproval: () => 'Pending',
    PendingClientApproval: () => 'Pending',
    Unspecified: () => '',
  })
}

/**
 * Maps the status to the tone it is shown in.
 */
export function formatStatusTone(
  status: ScopeChangeRequestStatus,
): ScopeChangeRequestStatusTone {
  return match(status, {
    Accepted: () => 'Accepted',
    Rejected: () => 'Rejected',
    ApplyFailed: () => 'Rejected',
    PendingFinalization: () => 'Pending',
    PendingAdminApproval: () => 'Pending',
    PendingClientApproval: () => 'Pending',
    Unspecified: () => 'Neutral',
  })
}

/**
 * Formats the status to a string that will be displayed in the table
 * @param status
 */
export function formatTableStatus(status: ScopeChangeRequestStatus) {
  return match(status, {
    Accepted: () => 'Accepted',
    Rejected: () => 'Rejected',
    ApplyFailed: () => 'System rejected',
    PendingFinalization: () => 'Reviewing',
    PendingAdminApproval: () => 'Reviewing',
    PendingClientApproval: () => 'Awaiting',
    Unspecified: () => '',
  })
}

/**
 * Formats the status to a string that will be displayed in the side panel
 * @param scopeChangeRequest
 */
export function formatSidePanelStatus(scopeChangeRequest: ScopeChangeRequest) {
  return match(scopeChangeRequest.status, {
    Accepted: () =>
      scopeChangeRequest.lastResponder?.responderType === 'Admin' ||
      (scopeChangeRequest.isCreatedByAdmin && scopeChangeRequest.isAccepted)
        ? 'Admin accepted'
        : 'Client accepted',
    Rejected: () =>
      scopeChangeRequest.lastResponder?.responderType === 'Admin'
        ? 'Admin rejected'
        : 'Client rejected',
    PendingFinalization: () => 'Reviewing',
    PendingAdminApproval: () => 'Admin approval required',
    PendingClientApproval: () => 'Client approval required',
    ApplyFailed: () => 'System rejected',
    Unspecified: () => '',
  })
}

/**
 * Formats the status to a string
 * @param scopeChangeRequest
 */
export function formatStatus(scopeChangeRequest: ScopeChangeRequest) {
  return match(scopeChangeRequest.status, {
    Accepted: () =>
      scopeChangeRequest.lastResponder?.responderType === 'Admin'
        ? 'Admin accepted'
        : 'Client accepted',
    Rejected: () =>
      scopeChangeRequest.lastResponder?.responderType === 'Admin'
        ? 'Admin rejected'
        : 'Client rejected',
    PendingFinalization: () => 'Pending finalization',
    PendingAdminApproval: () => 'Pending admin approval',
    PendingClientApproval: () => 'Pending client approval',
    ApplyFailed: () => 'System rejected',
    Unspecified: () => '',
  })
}

/**
 * Formats the number as USD currency and adds the sign (+,-) with a space before the sign
 * @param value
 */
export function formatSum(value: number) {
  return `${value >= 0 ? '+' : '-'} ${formatMoney(Math.abs(value))}`
}

/**
 * Formats the date to a string
 * @param date
 */
export function formatDate(date: Date) {
  return dateFormatters.dateLong.format(date)
}

/**
 * Formats when a request or response was sent, e.g. `01/31/2024 at 3:04 PM`.
 */
export function formatSentDate(date: Date) {
  return `${dateFormatters.dateAmerican.format(date)} at ${dateFormatters.timeDefault.format(date)}`
}

/**
 * Formats how long ago a request was opened.
 */
export function formatTimeOpen(createTime: Date) {
  return timeago(createTime, 'long')
}

/**
 * Formats which bundle a service item is included in, or `null` when the
 * item is not part of a bundle.
 */
export function formatBundleAttribution(
  item: ScopeChangePriceBreakDownItemForAdmin,
) {
  if (!item.isBundleIncludedItem) {
    return null
  }

  return item.bundleName
    ? `Included in bundle: ${item.bundleName}`
    : 'Included in bundle'
}
